import { Layout } from "@/components/layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { 
  X, 
  Link, 
  Download, 
  Tag, 
  Layers, 
  Wand2
} from "lucide-react";
import { useLocation, useRoute } from "wouter";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useQuery } from "@tanstack/react-query";
import type { Ad, Board } from "@shared/schema";

export default function AdDetails() {
  const [match, params] = useRoute("/ad/:id");
  const [, setLocation] = useLocation();

  const { data: ad, isLoading } = useQuery<Ad>({
    queryKey: ["/api/ads", params?.id],
    queryFn: async () => {
      const res = await fetch(`/api/ads/${params?.id}`);
      if (!res.ok) throw new Error("Ad not found");
      return res.json();
    },
    enabled: !!params?.id,
  });

  const { data: boards = [] } = useQuery<Board[]>({
    queryKey: ["/api/boards"],
  });

  if (!match) return null;

  if (isLoading) {
    return (
      <div className="fixed inset-0 z-50 bg-gray-100/50 backdrop-blur-sm flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!ad) {
    return (
      <div className="fixed inset-0 z-50 bg-gray-100/50 backdrop-blur-sm flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg p-8 text-center">
          <h2 className="text-lg font-bold text-gray-900 mb-2">Ad not found</h2>
          <p className="text-gray-500 mb-4">This ad may have been removed.</p>
          <Button onClick={() => setLocation("/find-ads")}>Back to Boards</Button>
        </div>
      </div>
    );
  }

  const runningDays = ad.startDate ? `${Math.max(1, Math.floor((Date.now() - new Date(ad.startDate).getTime()) / (1000 * 60 * 60 * 24)))} days` : "-";

  return (
    <div className="fixed inset-0 z-50 bg-gray-100/50 backdrop-blur-sm flex items-center justify-center p-4 md:p-8">
      <div className="bg-white rounded-xl shadow-2xl w-full h-full max-w-[1400px] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        
        <div className="h-16 border-b border-gray-100 flex items-center justify-between px-6 bg-white shrink-0">
          <div className="flex items-center gap-4">
            <Button 
              data-testid="button-close-ad-details"
              variant="ghost" 
              size="icon" 
              onClick={() => setLocation("/find-ads")}
              className="text-gray-500 hover:text-gray-900"
            >
              <X className="h-5 w-5" />
            </Button>
            <h1 className="text-lg font-bold text-gray-900">Ad Detail</h1>
          </div>
          
          <div className="flex items-center gap-3">
            <Button variant="outline" className="gap-2 text-gray-600 border-gray-200">
              <Download className="h-4 w-4" /> Unsave
            </Button>
            <Button variant="outline" className="gap-2 text-gray-600 border-gray-200">
              <Link className="h-4 w-4" /> Copy link
            </Button>
          </div>
        </div>

        <div className="flex-1 overflow-hidden grid grid-cols-1 lg:grid-cols-12 divide-x divide-gray-100 bg-gray-50/30">
          
          <div className="lg:col-span-4 p-8 overflow-y-auto">
             <div className="bg-white rounded-lg border border-gray-200 shadow-sm max-w-md mx-auto">
               <div className="p-3 flex items-center gap-3 border-b border-gray-50">
                 <div className="h-10 w-10 rounded-full bg-gray-100 overflow-hidden border border-gray-100 flex items-center justify-center">
                    {ad.brandLogo ? (
                      <img src={ad.brandLogo} alt={ad.brand} className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-sm font-bold text-primary">{ad.brand.charAt(0)}</span>
                    )}
                 </div>
                 <div className="flex-1">
                   <h3 data-testid="text-ad-brand" className="font-semibold text-gray-900 text-sm">{ad.brand}</h3>
                   <span className="text-xs text-gray-500 block">Sponsored</span>
                 </div>
               </div>

               {ad.copy && (
                 <div className="p-3 text-sm text-gray-800 whitespace-pre-line leading-relaxed">
                   {ad.copy}
                   <button className="text-gray-500 font-medium ml-1 hover:underline">Show more</button>
                 </div>
               )}

               <div className="relative aspect-[4/5] bg-gray-100 w-full">
                 {ad.image ? (
                   <img src={ad.image} alt="Ad Content" className="w-full h-full object-cover" />
                 ) : (
                   <div className="w-full h-full flex items-center justify-center text-gray-300 text-sm">No image</div>
                 )}
                 <Button variant="secondary" size="sm" className="absolute top-3 right-3 bg-white/90 backdrop-blur text-xs font-medium h-8 shadow-sm">
                    <Download className="mr-2 h-3.5 w-3.5" /> Download
                 </Button>
               </div>

               <div className="p-3 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
                  <div className="text-xs">
                     <div className="font-semibold text-gray-700">{ad.brand}</div>
                     <div className="text-gray-900 font-bold mt-0.5">{ad.copy ? ad.copy.substring(0, 50) + "..." : ""}</div>
                  </div>
                  <Button variant="secondary" size="sm" className="bg-gray-200 text-gray-800 font-semibold text-xs h-8 px-4 hover:bg-gray-300">
                    Shop now
                  </Button>
               </div>
             </div>
          </div>

          <div className="lg:col-span-4 p-8 overflow-y-auto bg-white">
             <div className="space-y-8 max-w-sm mx-auto">
                <div className="flex items-center justify-between">
                   <Badge variant="secondary" className={`px-2.5 py-0.5 text-xs font-semibold gap-1.5 ${
                     ad.status === "Active" 
                       ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border-emerald-100/50"
                       : "bg-gray-100 text-gray-600"
                   }`}>
                     <div className={`w-1.5 h-1.5 rounded-full ${ad.status === "Active" ? "bg-emerald-500" : "bg-gray-400"}`} /> {ad.status}
                   </Badge>
                   {ad.adExternalId && (
                     <div className="text-xs text-gray-400 flex items-center gap-1">
                       Ad ID: {ad.adExternalId} <Link className="h-3 w-3" />
                     </div>
                   )}
                </div>

                <div className="space-y-6">
                   <div className="space-y-1">
                     <label className="text-xs font-medium text-gray-500">Start date</label>
                     <div data-testid="text-ad-start-date" className="text-sm font-semibold text-gray-900">{ad.startDate || "-"}</div>
                   </div>

                   <div className="space-y-1">
                     <label className="text-xs font-medium text-gray-500">End date</label>
                     <div className="text-sm font-semibold text-gray-900">{ad.endDate || "-"}</div>
                   </div>

                   <div className="space-y-1">
                     <label className="text-xs font-medium text-gray-500">Running time</label>
                     <div className="text-sm font-semibold text-gray-900">{runningDays}</div>
                   </div>

                   <div className="space-y-1">
                     <label className="text-xs font-medium text-gray-500">Platforms</label>
                     <div className="flex gap-2 text-gray-400">
                        {(ad.platforms || []).map((p, i) => (
                          <div key={i} className="w-4 h-4 bg-gray-200 rounded-full" title={p} />
                        ))}
                        {(!ad.platforms || ad.platforms.length === 0) && <span className="text-sm text-gray-400">-</span>}
                     </div>
                   </div>

                   <div className="space-y-1">
                     <label className="text-xs font-medium text-gray-500">Display format</label>
                     <div className="text-sm font-semibold text-gray-900 capitalize">{ad.format}</div>
                   </div>

                   <div className="space-y-1">
                     <label className="text-xs font-medium text-gray-500">Categories</label>
                     <div className="text-sm font-semibold text-gray-900">{ad.categories || "-"}</div>
                   </div>
                </div>
             </div>
          </div>

          <div className="lg:col-span-4 p-8 overflow-y-auto">
            <div className="space-y-8 max-w-sm mx-auto">
              
              <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 space-y-4">
                 <h3 className="font-semibold text-gray-900">Save Details</h3>
                 
                 <div className="space-y-3">
                   <div className="space-y-1.5">
                     <label className="text-xs font-medium text-gray-700 flex items-center gap-1">
                        <Tag className="h-3 w-3" /> Tags
                     </label>
                     <Input data-testid="input-ad-tags" placeholder="Add tags..." className="bg-gray-50 text-sm h-9" />
                   </div>

                   <div className="space-y-1.5">
                     <label className="text-xs font-medium text-gray-700 flex items-center gap-1">
                        <Layers className="h-3 w-3" /> Boards
                     </label>
                     <Select>
                        <SelectTrigger data-testid="select-ad-board" className="bg-gray-50 h-9 text-sm">
                           <SelectValue placeholder="Select board" />
                        </SelectTrigger>
                        <SelectContent>
                           {boards.map((board) => (
                             <SelectItem key={board.id} value={String(board.id)}>{board.title}</SelectItem>
                           ))}
                        </SelectContent>
                     </Select>
                   </div>
                 </div>

                 <Button data-testid="button-confirm-save" className="w-full bg-[#E95333] hover:bg-[#D44222] text-white">Confirm</Button>
              </div>

              <div className="space-y-2">
                 <div className="flex items-center justify-between text-xs">
                    <span className="font-semibold text-gray-900">Atria AI</span>
                    <span className="text-gray-400">24,752 credits</span>
                 </div>
                 <Button data-testid="button-clone-ad" className="w-full h-12 bg-gradient-to-r from-gray-900 to-gray-700 hover:from-black hover:to-gray-800 text-white shadow-lg relative overflow-hidden group">
                    <div className="absolute inset-0 bg-white/10 translate-y-full group-hover:translate-y-0 transition-transform duration-300" />
                    <Wand2 className="mr-2 h-4 w-4" /> Clone ad
                 </Button>
              </div>

              <div className="space-y-3 pt-4 border-t border-gray-100">
                 <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-gray-900">Comments (0)</h3>
                    <span className="text-[10px] text-gray-400 uppercase tracking-wide">Only visible in workspace</span>
                 </div>
                 <Textarea 
                   data-testid="input-ad-comment"
                   placeholder="Add a comment..." 
                   className="min-h-[80px] bg-gray-50 border-gray-200 text-sm resize-none" 
                 />
              </div>

            </div>
          </div>

        </div>
      </div>
    </div>
  );
}
